﻿import useMap from '../../../hooks/use-map';
import { useCallback, useContext, useEffect, useRef } from 'react';
import WorkspaceItemContext from '../../../contexts/workspace/workspace-item-context';
import { useAppDispatch, useAppSelector } from '../../../hooks/store-hooks';
import { UDViewGeojsonPath } from '../../../../udView/udview';
import { getDirectoryDb } from '../../../../services/directory-db';
import { fetchAndValidateGeojsonUrl, readAndValidateGeojsonFile } from '../../../../services/geojson-validator';
import { FeatureCollectionWithProperties } from '@fe/maplibre-extensions';
import { patchFocusedWorkspace } from '../../../state/store';
import { useSafeEffect } from '../../../hooks/use-safe-effect';

export type GeojsonWarning = {
    index: number;
    message: string;
    permissionsMissing?: boolean;
};

interface FileState {
    handle: FileSystemFileHandle;
    permission: PermissionState;
    lastModified: number | null;
}

const FILE_POLL_INTERVAL_MS = 30 * 1000;

export default function CustomGeojsonProvider() {
    const map = useMap();
    const dispatch = useAppDispatch();
    const workspace = useContext(WorkspaceItemContext);
    const customGeojsonPlugin = workspace.customGeojsonPlugin;
    const paths = useAppSelector((state) => state.workspace.items[workspace.id].view.staticData.customGeojson.paths);
    const show = useAppSelector((state) => state.workspace.items[workspace.id].view.staticData.customGeojson.show);

    const loadedIds = useRef<Set<string>>(new Set());
    const loadedOrder = useRef<string[]>([]);
    const fileStateCache = useRef<Map<string, FileState>>(new Map());
    const activeLoads = useRef<Set<string>>(new Set());

    const dispatchWarning = useCallback(
        (index: number, message: string, permissionsMissing?: boolean) => {
            dispatch(
                patchFocusedWorkspace((w) =>
                    w.geojsonWarnings.push({
                        index,
                        message,
                        permissionsMissing
                    })
                )
            );
        },
        [dispatch]
    );

    // Use cache first, fall back to IDB + queryPermission.
    const resolveFileState = async (fileHandleKey: string): Promise<FileState | null> => {
        const cached = fileStateCache.current.get(fileHandleKey);
        if (cached) {
            return cached;
        }
        const db = await getDirectoryDb();
        const raw = await db.get('file-handles', fileHandleKey);
        if (!raw || raw.kind !== 'file') {
            return null;
        }
        const handle = raw as FileSystemFileHandle;
        const permission = await handle.queryPermission({ mode: 'read' });
        const state: FileState = { handle, permission, lastModified: null };
        fileStateCache.current.set(fileHandleKey, state);
        return state;
    };
    
    const readFileSource = async (
        fileState: FileState,
        sourceId: string,
        pathIndex: number,
        forceReload = false
    ): Promise<FeatureCollectionWithProperties | null> => {
        if (activeLoads.current.has(sourceId)) {
            return null;
        }

        let file: File;
        try {
            file = await fileState.handle.getFile();
        } catch {
            fileState.permission = 'denied';
            dispatchWarning(pathIndex, 'File can no longer be accessed');
            return null;
        }

        const lastModified = file.lastModified;
        if (!forceReload && fileState.lastModified === lastModified && loadedIds.current.has(sourceId)) {
            return null;
        }

        activeLoads.current.add(sourceId);
        try {
            const result = await readAndValidateGeojsonFile(fileState.handle);
            if (result.valid && result.data) {
                fileState.lastModified = lastModified;
                return result.data;
            } else {
                dispatchWarning(pathIndex, result.error ?? 'Invalid GeoJSON');
            }
        } finally {
            activeLoads.current.delete(sourceId);
        }
        return null;
    };

    useSafeEffect(() => {
        if (!map) {
            return;
        }

        const loadPaths = async () => {
            const currentOrder: string[] = [];
            const freshData = new Map<string, FeatureCollectionWithProperties>();

            dispatch(
                patchFocusedWorkspace((w) => {
                    w.geojsonWarnings = [];
                })
            );

            for (const path of paths) {
                if (!path.show || !show) {
                    continue;
                }

                const sourceId = getSourceId(path);
                const pathIndex = paths.indexOf(path);

                if (path.type === 'file') {
                    const fileState = await resolveFileState(path.fileHandle);
                    if (!fileState) {
                        dispatchWarning(pathIndex, 'File handle not found or invalid');
                        continue;
                    }

                    if (fileState.permission !== 'granted') {
                        dispatchWarning(pathIndex, 'Permission not granted', true);
                        continue;
                    }

                    const wasAlreadyLoaded = loadedIds.current.has(sourceId);
                    const data = await readFileSource(fileState, sourceId, pathIndex, !wasAlreadyLoaded);
                    if (data) {
                        freshData.set(sourceId, data);
                    }
                    if (data || wasAlreadyLoaded) {
                        currentOrder.push(sourceId);
                        loadedIds.current.add(sourceId);
                    }
                } else if (path.type === 'url') {
                    try {
                        new URL(path.url);
                    } catch {
                        dispatchWarning(pathIndex, 'Invalid URL');
                        continue;
                    }
                    const result = await fetchAndValidateGeojsonUrl(path.url);
                    if (result.valid && result.data) {
                        freshData.set(sourceId, result.data);
                        currentOrder.push(sourceId);
                        loadedIds.current.add(sourceId);
                    } else {
                        dispatchWarning(pathIndex, 'Failed to load GeoJSON from URL');
                    }
                }
            }

            const currentIds = new Set(currentOrder);

            const toRemove = [...loadedIds.current].filter((id) => !currentIds.has(id));
            for (const loadedId of toRemove) {
                customGeojsonPlugin.removeGeojson(loadedId);
                loadedIds.current.delete(loadedId);
            }

            const prevOrder = loadedOrder.current.filter((id) => currentIds.has(id));
            const orderChanged = prevOrder.length === currentOrder.length && currentOrder.some((id, i) => prevOrder[i] !== id);

            if (orderChanged) {
                for (const sourceId of currentOrder) {
                    customGeojsonPlugin.removeGeojson(sourceId);
                }
                for (const path of paths) {
                    if (!path.show || !show) {
                        continue;
                    }
                    const sourceId = getSourceId(path);
                    const pathIndex = paths.indexOf(path);
                    if (path.type === 'file') {
                        const fileState = fileStateCache.current.get(path.fileHandle);
                        if (fileState) {
                            const data = await readFileSource(fileState, sourceId, pathIndex, true);
                            if (data) {
                                customGeojsonPlugin.addGeojson(sourceId, data);
                            }
                        }
                    } else if (path.type === 'url') {
                        const result = await fetchAndValidateGeojsonUrl(path.url);
                        if (result.valid && result.data) {
                            customGeojsonPlugin.addGeojson(sourceId, result.data);
                        }
                    }
                }
            } else {
                for (const [sourceId, data] of freshData) {
                    customGeojsonPlugin.removeGeojson(sourceId);
                    customGeojsonPlugin.addGeojson(sourceId, data);
                }
            }

            loadedOrder.current = currentOrder;

            const activeHandleKeys = new Set(paths.filter((p) => p.type === 'file').map((p) => (p as { fileHandle: string }).fileHandle));
            for (const key of fileStateCache.current.keys()) {
                if (!activeHandleKeys.has(key)) {
                    fileStateCache.current.delete(key);
                }
            }
        };

        loadPaths();
    }, [map, paths, show, customGeojsonPlugin]);

    // Detects external file changes
    useEffect(() => {
        if (!map) {
            return;
        }

        const poll = async () => {
            for (const [fileHandleKey, fileState] of fileStateCache.current) {
                const sourceId = `file-${fileHandleKey}`;
                if (!loadedIds.current.has(sourceId)) {
                    continue;
                }
                const pathIndex = paths.findIndex((p) => p.type === 'file' && (p as { fileHandle: string }).fileHandle === fileHandleKey);
                const data = await readFileSource(fileState, sourceId, pathIndex);
                if (data) {
                    customGeojsonPlugin.removeGeojson(sourceId);
                    customGeojsonPlugin.addGeojson(sourceId, data);
                    if (pathIndex !== -1) {
                        dispatch(
                            patchFocusedWorkspace((w) => {
                                w.geojsonWarnings = w.geojsonWarnings.filter((warning) => warning.index !== pathIndex);
                            })
                        );
                    }
                }
            }
        };

        const intervalId = self.setInterval(poll, FILE_POLL_INTERVAL_MS);
        return () => self.clearInterval(intervalId);
    }, [map, customGeojsonPlugin, paths, dispatch]);

    useEffect(() => {
        return () => {
            for (const id of loadedIds.current) {
                customGeojsonPlugin.removeGeojson(id);
            }
            loadedIds.current.clear();
            loadedOrder.current = [];
            fileStateCache.current.clear();
        };
    }, [customGeojsonPlugin]);

    return null;
}

function getSourceId(path: UDViewGeojsonPath): string {
    if (path.type === 'file') {
        return `file-${path.fileHandle}`;
    }
    return `url-${encodeURIComponent(path.url)}`;
}
